import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Paperclip, UserCheck, Pencil, Gavel, Stamp, History, ListTree, Users, FileText, Send, BarChart3, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtDateTime, timeAgo, fmtMoney } from "@/lib/format";
import { InviteStatusBadge, EnquiryOpenBadge, ReviewStatusBadge, downloadFile } from "@/lib/enquiries";
import AuctionPanel from "@/components/AuctionPanel";
import ApprovalChainPanel from "@/components/ApprovalChainPanel";
import EventCountdown from "@/components/EventCountdown";
import CollapsibleSection from "@/components/CollapsibleSection";
import EditEventDialog from "@/components/EditEventDialog";
import { TermsView } from "@/components/TermsEditor";

export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [auctionInfo, setAuctionInfo] = useState(null);
  const [approvalInfo, setApprovalInfo] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState("details");
  const [showRemoved, setShowRemoved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ reviewer_ids: [], invite_ids: [], buyer_comment: "", deadline: "" });
  const [reinvite, setReinvite] = useState({ open: false, sellers: [], picked: [], confirm: false, sending: false, deadlinePassed: false });

  const openReinvite = async () => {
    try {
      const r = await api.get(`/enquiries/${id}/reinvite-eligibility`);
      const eligible = r.data.sellers.filter((s) => s.eligible).map((s) => s.invite_id);
      setReinvite({ open: true, sellers: r.data.sellers, picked: eligible, confirm: false, sending: false, deadlinePassed: r.data.deadline_passed });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendReinvite = async () => {
    setReinvite((r) => ({ ...r, sending: true }));
    try {
      const res = await api.post(`/enquiries/${id}/reinvite`, { invite_ids: reinvite.picked });
      toast.success(`Reminder sent to ${res.data.sent.length} seller(s)`);
      if (res.data.skipped?.length) toast.info(`Skipped: ${res.data.skipped.map((s) => s.supplier_name).join(", ")}`);
      setReinvite({ open: false, sellers: [], picked: [], confirm: false, sending: false, deadlinePassed: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setReinvite((r) => ({ ...r, sending: false, confirm: false }));
    }
  };
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignForm, setReassignForm] = useState({ new_reviewer_id: "", reason: "" });

  const load = useCallback(() => {
    api.get(`/enquiries/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get(`/enquiries/${id}/reviews`).then((r) => setReviews(r.data.reviews)).catch(() => {});
    api.get("/technical-reviewers").then((r) => setReviewers(r.data.reviewers)).catch(() => {});
    api.get(`/enquiries/${id}/auction`).then((r) => setAuctionInfo(r.data)).catch(() => setAuctionInfo(null));
    api.get(`/enquiries/${id}/approval`).then((r) => setApprovalInfo(r.data)).catch(() => setApprovalInfo(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex h-64 items-center justify-center" data-testid="enquiry-detail-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const { enquiry: enq, invites, events, attachments, versions, server_time } = data;

  const activeItems = (enq.items || []).filter((i) => i.status !== "removed");
  const removedItems = (enq.items || []).filter((i) => i.status === "removed");
  const activeInvites = invites.filter((i) => i.status !== "removed");
  const participated = invites.filter((i) => i.status === "participated");
  const pendingReviews = reviews.filter((r) => ["pending", "opened"].includes(r.status));
  const chain = approvalInfo?.chain;
  const auctionLive = auctionInfo && ["scheduled", "live", "extended"].includes(auctionInfo.auction?.status);
  const reviewByInvite = {};
  reviews.forEach((r) => { reviewByInvite[r.seller_response_id] = r; });
  const participantByCid = {};
  (auctionInfo?.participants || []).forEach((p) => { participantByCid[p.supplier_company_id] = p; });

  const openEdit = (tab) => { setEditTab(tab); setEditOpen(true); };

  const downloadPCS = async (format) => {
    try {
      const r = await api.get(`/enquiries/${id}/comparative-statement/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PCS-${enq.enquiry_number}.${format === "xlsx" ? "xlsx" : format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Comparative Statement downloaded (${format.toUpperCase()})`);
    } catch (err) {
      let msg = "Export failed";
      try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* noop */ }
      toast.error(msg);
    }
  };

  const sendForReview = async (e) => {
    e.preventDefault();
    if (!sendForm.reviewer_ids.length || !sendForm.invite_ids.length) return toast.error("Select at least one reviewer and one seller quotation");
    setSaving(true);
    try {
      const r = await api.post(`/enquiries/${id}/reviews`, { ...sendForm, deadline: sendForm.deadline ? new Date(sendForm.deadline).toISOString() : null });
      toast.success(`${r.data.created} technical review(s) assigned`);
      setSendOpen(false);
      setSendForm({ reviewer_ids: [], invite_ids: [], buyer_comment: "", deadline: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const doReassign = async (e) => {
    e.preventDefault();
    if (!reassignForm.new_reviewer_id) return toast.error("Select a new reviewer");
    setSaving(true);
    try {
      await api.post(`/enquiries/${id}/reviews/${reassignTarget.id}/reassign`, reassignForm);
      toast.success("Review reassigned");
      setReassignTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="enquiry-detail-page" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="icon" data-testid="enquiry-back-btn" onClick={() => navigate("/buyer/enquiries")}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight font-mono" data-testid="enquiry-number">{enq.enquiry_number}</h1>
            {enq.status === "draft"
              ? <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600" data-testid="enquiry-draft-badge">Draft</span>
              : <EnquiryOpenBadge passed={enq.deadline_passed} />}
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 font-mono text-xs font-semibold text-slate-600" data-testid="event-version-badge">
              Event Version: v{enq.version || 1}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Floated by {enq.created_by} · {fmtDateTime(enq.created_at)}{enq.last_modified_by ? ` · Last modified by ${enq.last_modified_by} ${timeAgo(enq.last_modified_at)}` : ""}</p>
        </div>
        {enq.status === "draft" && (
          <Button data-testid="publish-draft-btn" disabled={saving} onClick={async () => {
            setSaving(true);
            try { await api.post(`/enquiries/${id}/publish`); toast.success("Enquiry published — sellers invited"); load(); }
            catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
          }} className="bg-[#166534] hover:bg-[#14532D]"><Send className="mr-1.5 h-4 w-4" /> Publish Enquiry</Button>
        )}
        <Button data-testid="edit-event-btn" onClick={() => openEdit("details")} className="bg-slate-900 hover:bg-slate-800">
          <Pencil className="mr-1.5 h-4 w-4" /> Edit Event
        </Button>
        <Button variant="outline" data-testid="download-enquiry-pdf-btn" onClick={() => downloadFile(`/enquiries/${id}/download`, `${enq.enquiry_number}.pdf`)}>
          <Download className="mr-1.5 h-4 w-4" /> Download Enquiry PDF
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-lg border bg-card px-5 py-4 md:grid-cols-4 lg:grid-cols-8" data-testid="event-summary-strip">
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</p><p className="mt-0.5 font-mono text-sm font-semibold" data-testid="summary-status">{enq.event_state || (enq.deadline_passed ? "CLOSED" : "LIVE")}</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Line Items</p><p className="mt-0.5 font-mono text-sm font-semibold" data-testid="summary-items">{activeItems.length}</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Sellers</p><p className="mt-0.5 font-mono text-sm font-semibold" data-testid="summary-sellers">{activeInvites.length}</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Responses</p><p className="mt-0.5 font-mono text-sm font-semibold" data-testid="summary-responses">{participated.length}</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Tech Reviews</p><p className="mt-0.5 font-mono text-sm font-semibold" data-testid="summary-reviews">{pendingReviews.length} pending</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Auction</p><p className="mt-0.5 text-sm font-semibold capitalize" data-testid="summary-auction">{auctionInfo ? `${auctionInfo.auction.status.replace(/_/g, " ")} (R${auctionInfo.round_number || 1})` : "Not started"}</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Approval</p><p className="mt-0.5 text-sm font-semibold capitalize" data-testid="summary-approval">{chain ? chain.status.replace(/_/g, " ") : "Not started"}</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Version</p><p className="mt-0.5 font-mono text-sm font-semibold" data-testid="summary-version">v{enq.version || 1}</p></div>
      </div>

      <CollapsibleSection id={`inquiry-${id}`} title="Inquiry Details" icon={<FileText className="h-4 w-4 text-slate-500" />}
        defaultOpen testid="section-inquiry-details"
        actions={<Button variant="outline" size="sm" data-testid="section-edit-event-btn" onClick={() => openEdit("details")}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 pt-4 md:grid-cols-4">
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Event ID</p><p className="mt-1 font-mono text-sm font-semibold">{enq.enquiry_number}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Timer Ends</p><p className="mt-1 text-sm font-semibold" data-testid="enquiry-deadline">{fmtDateTime(enq.deadline)}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Incoterms</p><p className="mt-1 font-mono text-sm">{enq.incoterms}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery Required</p><p className="mt-1 text-sm">{enq.delivery_days} days</p></div>
          <EventCountdown deadline={enq.deadline} serverTime={server_time} label="Time Remaining" testid="buyer-event-countdown" />
          <div className="col-span-2 md:col-span-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Description / Buyer Instructions</p><p className="mt-1 text-sm">{enq.description}</p></div>
          {enq.title && <div className="col-span-2"><p className="text-xs uppercase tracking-wide text-muted-foreground">Event Title</p><p className="mt-1 text-sm font-medium" data-testid="enquiry-title">{enq.title}</p></div>}
          {enq.start_at && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Event Start</p><p className="mt-1 text-sm" data-testid="enquiry-start">{fmtDateTime(enq.start_at)}</p></div>}
        </div>
        {(enq.delivery_address?.line1 || enq.buyer_terms_conditions || enq.buyer_comment) && (
          <div className="grid grid-cols-1 gap-4 border-t px-5 py-4 md:grid-cols-2">
            {enq.delivery_address?.line1 && (
              <div data-testid="buyer-delivery-address">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery Address</p>
                <p className="mt-1 text-sm">
                  {enq.delivery_address.line1}{enq.delivery_address.line2 ? `, ${enq.delivery_address.line2}` : ""}<br />
                  {enq.delivery_address.city}, {enq.delivery_address.state}, {enq.delivery_address.country} — {enq.delivery_address.postal_code}
                  {enq.delivery_address.contact_name && <span className="block text-xs text-muted-foreground">Contact: {enq.delivery_address.contact_name} {enq.delivery_address.contact_number} {enq.delivery_address.contact_email}</span>}
                </p>
              </div>
            )}
            {enq.buyer_terms_conditions && (
              <div data-testid="buyer-terms-block">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Buyer Terms &amp; Conditions <span className="font-mono">v{enq.terms_version || 1}</span></p>
                <div className="mt-1"><TermsView text={enq.buyer_terms_conditions} testid="buyer-terms-view" /></div>
              </div>
            )}
            {enq.buyer_comment && (
              <div data-testid="buyer-comment-block">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Buyer Comment</p>
                <p className="mt-1 text-sm italic">"{enq.buyer_comment}"</p>
              </div>
            )}
          </div>
        )}
        <div className="overflow-x-auto px-5 pt-4">
          <table className="w-full text-sm" data-testid="enquiry-items-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Material Code</th>
                <th className="py-2 pr-4 font-medium">Description</th>
                <th className="py-2 pr-4 font-medium text-right">Qty</th>
                <th className="py-2 pr-4 font-medium">UOM</th>
                <th className="py-2 pr-4 font-medium">Incoterms</th>
                <th className="py-2 pr-4 font-medium text-right">Delivery Days</th>
                <th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(showRemoved ? enq.items : activeItems).map((it, i) => (
                <tr key={it.id || i} className={`border-b last:border-0 ${it.status === "removed" ? "bg-slate-50 text-slate-400" : ""}`} data-testid={`enquiry-item-row-${i}`}>
                  <td className="py-2.5 pr-4 font-mono text-xs text-muted-foreground">{i + 1}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs font-semibold">{it.material_code}</td>
                  <td className="py-2.5 pr-4">{it.description}{it.status === "removed" && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase">Removed</span>}</td>
                  <td className="py-2.5 pr-4 text-right font-mono">{it.quantity}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs">{it.uom}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs">{it.incoterms || enq.incoterms}</td>
                  <td className="py-2.5 pr-4 text-right font-mono text-xs">{it.delivery_days || enq.delivery_days}</td>
                  <td className="py-2.5 text-right">
                    {it.status !== "removed" && (
                      <Button variant="ghost" size="sm" data-testid={`item-edit-btn-${it.material_code}`} onClick={() => openEdit("items")}>
                        <Pencil className="mr-1 h-3 w-3" /> Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center gap-2 py-3">
            <Button variant="outline" size="sm" data-testid="add-line-item-quick-btn" onClick={() => openEdit("items")}>+ Add Line Item</Button>
            {removedItems.length > 0 && (
              <Button variant="ghost" size="sm" data-testid="show-removed-items-btn" onClick={() => setShowRemoved(!showRemoved)}>
                {showRemoved ? "Hide Removed Items" : `Show Removed Items (${removedItems.length})`}
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t px-5 py-3">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Attachments</span>
          {attachments.map((f) => (
            <button key={f.id} data-testid={`enquiry-attachment-${f.id.slice(0, 8)}`} onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}
              className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-slate-50">
              <Paperclip className="h-3 w-3" /> {f.filename}
            </button>
          ))}
          <Button variant="outline" size="sm" data-testid="manage-attachments-btn" onClick={() => openEdit("attachments")}><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Manage</Button>
        </div>
      </CollapsibleSection>

      <CollapsibleSection id={`sellers-${id}`} title={`Sellers (${activeInvites.length})`} icon={<Users className="h-4 w-4 text-slate-500" />} defaultOpen testid="section-sellers"
        actions={<div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" data-testid="comparative-statement-btn" className="bg-slate-900 hover:bg-slate-800">
                <BarChart3 className="mr-1 h-3.5 w-3.5" /> Comparative Statement
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white" align="end">
              <DropdownMenuItem data-testid="pcs-download-pdf" onClick={() => downloadPCS("pdf")}>
                <Download className="mr-2 h-3.5 w-3.5" /> Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="pcs-export-xlsx" onClick={() => downloadPCS("xlsx")}>
                <FileSpreadsheet className="mr-2 h-3.5 w-3.5" /> Export Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="pcs-export-csv" onClick={() => downloadPCS("csv")}>
                <FileText className="mr-2 h-3.5 w-3.5" /> Export CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" data-testid="reinvite-sellers-btn" onClick={openReinvite}><Send className="mr-1.5 h-3.5 w-3.5" /> Re-invite Sellers</Button>
          <Button variant="outline" size="sm" data-testid="add-supplier-quick-btn" onClick={() => openEdit("suppliers")}>+ Add Supplier</Button>
        </div>}>
        {participated.length > 0 && (
          <div className="overflow-x-auto border-b px-5 py-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Seller Responses</p>
            <table className="w-full text-sm" data-testid="seller-responses-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Seller</th>
                  <th className="py-2 pr-4 font-medium">Response Status</th>
                  <th className="py-2 pr-4 font-medium text-right">Total Base Value</th>
                  <th className="py-2 pr-4 font-medium text-right">GST</th>
                  <th className="py-2 pr-4 font-medium text-right">Total Final Value</th>
                  <th className="py-2 pr-4 font-medium text-right">Delivery Days</th>
                  <th className="py-2 pr-4 font-medium">Technical Status</th>
                  <th className="py-2 font-medium">Auction Status</th>
                </tr>
              </thead>
              <tbody>
                {participated.map((inv) => {
                  const r = inv.response || {};
                  const rev = reviewByInvite[inv.id];
                  const part = participantByCid[inv.supplier_company_id];
                  return (
                    <tr key={inv.id} className="border-b last:border-0" data-testid={`response-row-${inv.supplier_name.replace(/\W+/g, "-").toLowerCase()}`}>
                      <td className="py-2.5 pr-4 font-medium">{inv.supplier_name}</td>
                      <td className="py-2.5 pr-4">
                        {inv.response_review_required
                          ? <span className="rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold uppercase text-[#92400E]" data-testid={`response-review-required-${inv.id.slice(0, 8)}`}>Response Requires Review</span>
                          : <InviteStatusBadge status={inv.status} />}
                      </td>
                      <td className="py-2.5 pr-4 text-right font-mono text-xs">{fmtMoney(r.base_value)}</td>
                      <td className="py-2.5 pr-4 text-right font-mono text-xs">{r.gst_pct}% ({fmtMoney(r.gst_amount)})</td>
                      <td className="py-2.5 pr-4 text-right font-mono text-xs font-semibold">{fmtMoney(r.final_value)}</td>
                      <td className="py-2.5 pr-4 text-right font-mono text-xs">{r.delivery_days}d</td>
                      <td className="py-2.5 pr-4 text-xs">{rev ? <ReviewStatusBadge status={rev.status} displayStatus={rev.display_status} /> : "—"}</td>
                      <td className="py-2.5 text-xs">{part ? (part.final_status || part.status) + (part.rank ? ` · #${part.rank}` : "") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="divide-y" data-testid="invited-sellers-list">
          {invites.map((inv) => (
            <div key={inv.id} data-testid={`invited-seller-${inv.supplier_name.replace(/\W+/g, "-").toLowerCase()}`} className="px-5 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{inv.supplier_name}</p>
                  <p className="text-xs text-muted-foreground">{inv.supplier_email}{inv.opened_at ? ` · Opened ${timeAgo(inv.opened_at)}` : ""}</p>
                </div>
                <InviteStatusBadge status={inv.status} />
                {enq.buyer_terms_conditions?.trim() ? (
                  inv.terms_accepted
                    ? <span className="rounded-full border border-[#BBF7D0] bg-[#DCFCE7] px-2 py-0.5 text-[10px] font-semibold text-[#166534]" data-testid={`terms-accepted-${inv.id.slice(0, 8)}`}>T&C v{inv.terms_accepted_version} ✓</span>
                    : <span className="rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold text-[#92400E]" data-testid={`terms-pending-${inv.id.slice(0, 8)}`}>T&C pending</span>
                ) : null}
                {inv.status === "participated" && (
                  <Button variant="outline" size="sm" data-testid={`download-offer-${inv.id.slice(0, 8)}`}
                    onClick={() => downloadFile(`/enquiries/${id}/offers/${inv.id}/download`, `offer-${inv.supplier_name.replace(/\s+/g, "_")}-${enq.enquiry_number}.pdf`)}>
                    <Download className="mr-1.5 h-3.5 w-3.5" /> Offer PDF
                  </Button>
                )}
              </div>
              {inv.status === "participated" && inv.response && (
                <div className="mt-3 rounded-md border border-[#BBF7D0] bg-[#DCFCE7]/30 p-4" data-testid={`offer-details-${inv.id.slice(0, 8)}`}>
                  {inv.response_review_required && (
                    <p className="mb-2 rounded-md border border-[#FDE68A] bg-[#FEF3C7] px-3 py-1.5 text-xs font-medium text-[#92400E]" data-testid={`offer-review-note-${inv.id.slice(0, 8)}`}>
                      RESPONSE REQUIRES REVIEW — {inv.response_review_note || "the event was modified after this quotation."}
                    </p>
                  )}
                  {inv.response.items && (
                    <table className="mb-3 w-full text-sm" data-testid={`offer-items-${inv.id.slice(0, 8)}`}>
                      <thead><tr className="border-b border-[#BBF7D0] text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="py-1.5 pr-4 font-medium">Material</th><th className="py-1.5 pr-4 font-medium text-right">Qty</th>
                        <th className="py-1.5 pr-4 font-medium text-right">Unit Price</th><th className="py-1.5 font-medium text-right">Base Value</th>
                      </tr></thead>
                      <tbody>
                        {inv.response.items.map((oi, i) => {
                          const enqItem = activeItems.find((x) => x.material_code === oi.material_code) || {};
                          return (
                            <tr key={oi.material_code} data-testid={`offer-item-${inv.id.slice(0, 8)}-${i}`} className="border-b border-[#BBF7D0]/60 last:border-0">
                              <td className="py-1.5 pr-4 font-mono text-xs">{oi.material_code}</td>
                              <td className="py-1.5 pr-4 text-right font-mono text-xs">{enqItem.quantity} {enqItem.uom}</td>
                              <td className="py-1.5 pr-4 text-right font-mono text-xs font-semibold">{fmtMoney(oi.unit_price)}</td>
                              <td className="py-1.5 text-right font-mono text-xs">{fmtMoney(oi.line_value ?? (enqItem.quantity ? oi.unit_price * enqItem.quantity : null))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Packaging</p><p className="mt-0.5 font-mono text-sm">{fmtMoney(inv.response.packaging_cost)}</p></div>
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Freight</p><p className="mt-0.5 font-mono text-sm">{fmtMoney(inv.response.freight_cost)}</p></div>
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Total Base Value</p><p className="mt-0.5 font-mono text-sm font-semibold" data-testid={`offer-base-${inv.id.slice(0, 8)}`}>{fmtMoney(inv.response.base_value)}</p></div>
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">GST ({inv.response.gst_pct}%)</p><p className="mt-0.5 font-mono text-sm">{fmtMoney(inv.response.gst_amount)}</p></div>
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Total Final Value</p><p className="mt-0.5 font-mono text-sm font-bold text-[#166534]" data-testid={`offer-final-${inv.id.slice(0, 8)}`}>{fmtMoney(inv.response.final_value)}</p></div>
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery</p><p className="mt-0.5 font-mono text-sm">{inv.response.delivery_days} days</p></div>
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Incoterms</p><p className="mt-0.5 font-mono text-sm">{inv.response.incoterms}</p></div>
                    <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Payment Terms</p><p className="mt-0.5 text-sm">{inv.response.payment_terms}</p></div>
                    <div className="col-span-2 md:col-span-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Technical Specifications</p><p className="mt-0.5 text-sm">{inv.response.tech_specs}</p></div>
                    {inv.response.comment && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Comments</p><p className="mt-0.5 text-sm">{inv.response.comment}</p></div>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection id={`technical-${id}`} title="Technical Evaluation" icon={<UserCheck className="h-4 w-4 text-slate-500" />}
        defaultOpen={pendingReviews.length > 0 || reviews.length === 0} testid="section-technical"
        badge={pendingReviews.length > 0 && <span className="rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold text-[#92400E]" data-testid="tech-pending-badge">{pendingReviews.length} pending</span>}
        actions={<Button size="sm" data-testid="send-for-review-btn" disabled={!participated.length} onClick={() => setSendOpen(true)} className="bg-slate-900 hover:bg-slate-800">
          <UserCheck className="mr-1.5 h-4 w-4" /> Send for Technical Review
        </Button>}>
        {reviews.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="reviews-empty">No technical reviews assigned yet. Send submitted seller quotations to a technical reviewer for evaluation.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="technical-reviews-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-2.5 font-medium">Seller</th>
                  <th className="px-4 py-2.5 font-medium">Reviewer</th>
                  <th className="px-4 py-2.5 font-medium">Assigned On</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Decision</th>
                  <th className="px-4 py-2.5 font-medium">Reviewed On</th>
                  <th className="px-4 py-2.5 font-medium">Comments</th>
                  <th className="px-4 py-2.5 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r) => (
                  <tr key={r.id} data-testid={`review-row-${r.id.slice(0, 8)}`} className="border-b last:border-0">
                    <td className="px-5 py-3 font-medium">{r.supplier_name}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{r.reviewer_name}</p>
                      <p className="text-xs text-muted-foreground">{r.reviewer_designation} · {r.reviewer_department}</p>
                    </td>
                    <td className="px-4 py-3 text-xs">{fmtDateTime(r.assigned_at)}</td>
                    <td className="px-4 py-3"><ReviewStatusBadge status={r.status} displayStatus={r.display_status} /></td>
                    <td className="px-4 py-3 text-xs capitalize">{r.status === "pending" || r.status === "opened" ? "—" : r.decision}</td>
                    <td className="px-4 py-3 text-xs">{r.reviewed_at ? fmtDateTime(r.reviewed_at) : "—"}</td>
                    <td className="max-w-44 px-4 py-3"><p className="truncate text-xs" title={r.reviewer_comment}>{r.reviewer_comment || "—"}</p></td>
                    <td className="px-4 py-3 text-right">
                      {(r.status === "pending" || r.status === "opened") && (
                        <Button variant="outline" size="sm" data-testid={`reassign-${r.id.slice(0, 8)}`}
                          onClick={() => { setReassignTarget(r); setReassignForm({ new_reviewer_id: "", reason: "" }); }}>
                          Reassign
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection id={`auction-${id}`} title="Auction" icon={<Gavel className="h-4 w-4 text-slate-500" />} defaultOpen={!!auctionLive} testid="section-auction"
        badge={auctionLive && <span className="rounded-full border border-[#BBF7D0] bg-[#DCFCE7] px-2 py-0.5 text-[10px] font-semibold text-[#166534]" data-testid="auction-live-badge">ACTIVE</span>}>
        <AuctionPanel enquiryId={id} />
      </CollapsibleSection>

      <CollapsibleSection id={`approval-${id}`} title="Approval" icon={<Stamp className="h-4 w-4 text-slate-500" />}
        defaultOpen={!!chain && ["in_progress", "review_required"].includes(chain.status)} testid="section-approval"
        badge={chain && ["in_progress", "review_required"].includes(chain.status) && <span className="rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold text-[#92400E]" data-testid="approval-attention-badge">ACTION NEEDED</span>}>
        <ApprovalChainPanel enquiryId={id} invites={invites} />
      </CollapsibleSection>

      <CollapsibleSection id={`versions-${id}`} title="Event Version History" icon={<History className="h-4 w-4 text-slate-500" />} defaultOpen={false} testid="section-versions"
        badge={<span className="font-mono text-xs text-muted-foreground">v{enq.version || 1}</span>}>
        <div className="divide-y" data-testid="version-history-list">
          {(versions || []).map((v) => (
            <div key={v.id} className="flex flex-wrap items-start gap-3 px-5 py-3" data-testid={`version-row-${v.version}`}>
              <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-xs font-semibold text-white">v{v.version}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{v.summary}</p>
                <p className="text-xs text-muted-foreground">{v.changed_by} · {fmtDateTime(v.changed_at)}</p>
                {(v.changes || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {v.changes.map((c) => (
                      <span key={`${c.field}-${String(c.old)}-${String(c.new)}`} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                        {c.field}{c.old !== undefined ? `: ${String(c.old)} → ${String(c.new)}` : c.action ? ` ${c.action}` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection id={`eventflow-${id}`} title="Event Flow" icon={<ListTree className="h-4 w-4 text-slate-500" />} defaultOpen={false} testid="section-eventflow">
        <div className="divide-y" data-testid="enquiry-event-flow">
          {events.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">No events yet.</p>}
          {events.map((ev) => (
            <div key={ev.id} data-testid={`enquiry-event-${ev.id.slice(0, 8)}`} className="flex items-start gap-3 px-5 py-3">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${ev.actor_role === "supplier" ? "bg-blue-500" : "bg-slate-900"}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm">{ev.message}</p>
                <p className="text-xs text-muted-foreground">{ev.actor_name} · {ev.actor_role} · {timeAgo(ev.created_at)} · {fmtDateTime(ev.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <EditEventDialog open={editOpen} onOpenChange={setEditOpen} enquiryId={id} data={data} reviews={reviews} initialTab={editTab} onSaved={load} />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-white sm:max-w-xl" data-testid="send-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Quotations for Technical Review</DialogTitle>
            <DialogDescription>Reviewers see technical details only — pricing is never shared with them.</DialogDescription>
          </DialogHeader>
          <form onSubmit={sendForReview} className="space-y-4">
            <div>
              <Label>Select Technical Reviewer(s) *</Label>
              <div className="mt-1.5 max-h-36 space-y-1 overflow-y-auto rounded-md border p-2" data-testid="review-reviewer-list">
                {reviewers.length === 0 && <p className="p-2 text-sm text-muted-foreground">No reviewers yet — add them under Technical Reviewers.</p>}
                {reviewers.map((r) => (
                  <label key={r.id} className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                    <Checkbox data-testid={`review-select-reviewer-${r.id.slice(0, 8)}`} checked={sendForm.reviewer_ids.includes(r.id)}
                      onCheckedChange={() => setSendForm((f) => ({ ...f, reviewer_ids: f.reviewer_ids.includes(r.id) ? f.reviewer_ids.filter((x) => x !== r.id) : [...f.reviewer_ids, r.id] }))} />
                    <span className="font-medium">{r.name}</span>
                    <span className="text-xs text-muted-foreground">{r.department}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Select Seller Quotation(s) *</Label>
              <div className="mt-1.5 max-h-36 space-y-1 overflow-y-auto rounded-md border p-2" data-testid="review-seller-list">
                {participated.map((i) => (
                  <label key={i.id} className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                    <Checkbox data-testid={`review-select-seller-${i.id.slice(0, 8)}`} checked={sendForm.invite_ids.includes(i.id)}
                      onCheckedChange={() => setSendForm((f) => ({ ...f, invite_ids: f.invite_ids.includes(i.id) ? f.invite_ids.filter((x) => x !== i.id) : [...f.invite_ids, i.id] }))} />
                    <span className="font-medium">{i.supplier_name}</span>
                    <span className="text-xs text-muted-foreground">submitted {timeAgo(i.responded_at)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Buyer Comment (optional)</Label><Textarea data-testid="review-buyer-comment" rows={2} className="mt-1.5" placeholder="Please verify whether the proposed material specifications comply with our technical requirements." value={sendForm.buyer_comment} onChange={(e) => setSendForm({ ...sendForm, buyer_comment: e.target.value })} /></div>
            <div><Label>Technical Review Deadline (optional)</Label><Input data-testid="review-deadline-input" type="datetime-local" className="mt-1.5" value={sendForm.deadline} onChange={(e) => setSendForm({ ...sendForm, deadline: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="review-send-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send for Technical Review
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignTarget} onOpenChange={() => setReassignTarget(null)}>
        <DialogContent className="bg-white" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reassign Reviewer</DialogTitle>
            <DialogDescription>Reassign the {reassignTarget?.supplier_name} quotation currently with {reassignTarget?.reviewer_name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doReassign} className="space-y-4">
            <div>
              <Label>New Technical Reviewer *</Label>
              <div className="mt-1.5 space-y-1 rounded-md border p-2" data-testid="reassign-reviewer-list">
                {reviewers.filter((r) => r.id !== reassignTarget?.technical_reviewer_id).map((r) => (
                  <label key={r.id} className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                    <input type="radio" name="new-reviewer" data-testid={`reassign-select-${r.id.slice(0, 8)}`} checked={reassignForm.new_reviewer_id === r.id}
                      onChange={() => setReassignForm({ ...reassignForm, new_reviewer_id: r.id })} className="accent-slate-900" />
                    <span className="font-medium">{r.name}</span>
                    <span className="text-xs text-muted-foreground">{r.department}</span>
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Reason</Label><Input data-testid="reassign-reason" className="mt-1.5" placeholder="e.g. Reviewer on leave" value={reassignForm.reason} onChange={(e) => setReassignForm({ ...reassignForm, reason: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReassignTarget(null)}>Cancel</Button>
              <Button type="submit" data-testid="reassign-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reassign
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={reinvite.open} onOpenChange={(v) => setReinvite((r) => ({ ...r, open: v, confirm: false }))}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="reinvite-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Re-invite Sellers</DialogTitle>
            <DialogDescription>Send a response reminder to sellers who have not yet responded. Sellers who already submitted or declined are not eligible.</DialogDescription>
          </DialogHeader>
          {reinvite.deadlinePassed && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="reinvite-expired">
              The event timer has expired — extend the timeline before re-inviting sellers.
            </p>
          )}
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border p-2" data-testid="reinvite-seller-list">
            {reinvite.sellers.map((s) => (
              <label key={s.invite_id} className={`flex items-start gap-2.5 rounded px-2 py-2 ${s.eligible ? "cursor-pointer hover:bg-slate-50" : "opacity-60"}`} data-testid={`reinvite-row-${s.invite_id.slice(0, 8)}`}>
                <Checkbox disabled={!s.eligible} checked={reinvite.picked.includes(s.invite_id)} data-testid={`reinvite-check-${s.invite_id.slice(0, 8)}`}
                  onCheckedChange={(v) => setReinvite((r) => ({ ...r, picked: v ? [...r.picked, s.invite_id] : r.picked.filter((x) => x !== s.invite_id) }))} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{s.supplier_name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {s.invitation_status === "invited" ? "Invited · Response pending" : s.invitation_status === "opened" ? "Opened · No response" : s.invitation_status}
                    {s.last_reminded_at ? ` · Last reminder ${timeAgo(s.last_reminded_at)}` : s.created_at ? ` · Last invited ${timeAgo(s.created_at)}` : ""}
                    {s.ineligible_reason ? ` · ${s.ineligible_reason}` : ""}
                  </span>
                </span>
              </label>
            ))}
            {reinvite.sellers.length === 0 && <p className="p-3 text-sm text-muted-foreground">No sellers on this event yet.</p>}
          </div>
          {reinvite.confirm ? (
            <div className="space-y-3" data-testid="reinvite-confirm">
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                You are about to send a reminder to {reinvite.picked.length} Seller{reinvite.picked.length === 1 ? "" : "s"}.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" data-testid="reinvite-back-btn" onClick={() => setReinvite((r) => ({ ...r, confirm: false }))}>Back</Button>
                <Button className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={reinvite.sending} onClick={sendReinvite} data-testid="reinvite-confirm-send-btn">
                  {reinvite.sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm &amp; Send
                </Button>
              </div>
            </div>
          ) : (
            <Button className="w-full bg-slate-900 hover:bg-slate-800" disabled={!reinvite.picked.length || reinvite.deadlinePassed}
              onClick={() => setReinvite((r) => ({ ...r, confirm: true }))} data-testid="reinvite-send-btn">
              Send Reminder{reinvite.picked.length ? ` (${reinvite.picked.length})` : ""}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
